// Schritt 6 des Übergabe-Assistenten — Unterschriften: der rechtlich heikelste
// Moment der ganzen Erfassung. Je Partei (Übergeber, Übernehmer, beliebig viele
// Zeugen) eine Karte mit Name/Ort/Datum als Entwurfsfelder — erst der Druck auf
// „Unterschreiben" samt gezeichneter Signatur macht daraus einen unveränderlichen
// Eintrag in entwurf.protokoll.unterschriften. Vor dem Signieren ist nichts
// „gespeichert" im Sinne des Protokolls; das entspricht dem Verhalten eines echten
// Unterschriftenfelds (nichts zählt, bis unterschrieben wurde) und erspart eine
// zweite Quelle der Wahrheit für den ENDGÜLTIGEN Namen/Ort/Datum neben den Parteien
// aus Schritt 1.
//
// Fix-Runde 1 (Review nach Task 9): Die editierbaren Entwurfsfelder selbst lagen
// zunächst in einem lokalen useState dieser Komponente — der Assistent rendert je
// Schritt aber eine andere Komponente an derselben Stelle, sodass ein Wechsel weg von
// Schritt 6 und zurück die Komponente neu aufbaut und jede noch nicht signierte
// Korrektur (z. B. ein vor Ort berichtigter Zeugenname) verloren ging. Die Felder
// liegen jetzt in entwurf.unterschrift_entwuerfe (lib/uebergabe/lokal.ts) und werden
// wie jedes andere Entwurfsfeld über setEntwurf geschrieben — sie überstehen damit
// Schrittwechsel, Reload und (über den in UebergabeAssistent.tsx erweiterten
// localStorage-Notanker) auch einen harten Reload kurz nach der letzten Eingabe.
// effektiveFelder() liefert dabei entweder den gespeicherten Entwurfswert oder,
// solange noch nichts eingegeben wurde, denselben Default wie zuvor (Name aus
// Schritt 1, Ort noch leer, Datum = heute).
//
// Task 3 (mehrere Zeugen): Alle bis dahin nach ROLLE geschlüsselten Zustände
// (aktivesSignierfeld, neuUnterschreiben, unterschrift_entwuerfe, die Suche nach
// vorhandenen Unterschriften) sind auf die PARTEI-KENNUNG (`pid`) umgestellt — sonst
// teilen sich zwei Zeugen dieselben Ort-/Datumsfelder, weil die Rolle "zeuge" sie
// nicht mehr eindeutig identifiziert. Der Compiler warnt dabei NICHT: der
// Schlüsseltyp von unterschrift_entwuerfe ist bereits `string` (Task 2), ein
// Rollen-String bleibt also weiterhin typkorrekt, nur fachlich falsch. Als
// Kartenschlüssel dient überall `partei.pid ?? partei.rolle` — der Rückfall auf die
// Rolle greift nur bei Altdaten ohne `pid` (vervollstaendigeEntwurf/ergaenzeKennungen
// rüsten sie beim Laden nach, s. lib/uebergabe/unterschriften.ts).
//
// Ort wird beim Öffnen dieses Schritts aus der Stadt des gewählten Objekts
// vorbelegt (eigener kleiner Fetch — Schritte bekommen laut Vorgabe nur
// { entwurf, setEntwurf }, siehe UebergabeAssistent.tsx). Datum ist standardmäßig
// heute, im Anzeigeformat TT.MM.JJJJ — analog zur selben Konvention in
// pages/public/SignierenSeite.tsx (Commit „Datumsfelder in deutschem Format"): die
// PDF-Finalisierung druckt u.datum unverändert, es gibt dort kein Sicherheitsnetz
// wie bei renoviert_am.
//
// Der Abschluss-Knopf im Assistenten ist gesperrt, solange Übergeber und
// Übernehmer nicht signiert haben (Zeugen freiwillig) — geprüft über
// pruefeAbschlussBereit() aus lib/uebergabe/unterschriften.ts, damit Sperr-Logik und
// Sperrgrund-Text an genau einer Stelle definiert sind. Diese Regel bleibt in Task 3
// UNVERÄNDERT: Zeugen blockieren nie einen Abschluss, egal wie viele es gibt. Seit
// dem Abschluss-Review liegt dieselbe Regel zusätzlich in schliesseAb()
// (lib/uebergabe/abschluss.ts): die Oberfläche kann sperren, verhindern muss es der
// Abschluss selbst — „Jetzt senden" in der Liste kommt an dieser Komponente vorbei.
// Der „Unterschreiben"-Knopf je Karte ist zusätzlich gesperrt, solange der Name leer
// ist (Fix-Runde 1, Punkt 4) — eine Signatur ohne Namen ist in einem Beweisdokument
// wertlos.
//
// Optik (design_handoff_uebergabe §4.6, Screens 13–15): je Partei eine Karte mit Rollen-Symbol,
// unterschrieben = grüner Ring + Chip „Unterschrieben“, Bereitschaft als Hinweis-Zeile darunter.
// Das Unterschriftsfeld öffnet als Sheet (UnterschriftPad huelle="sheet"). Zeugen-Entfernen fragt
// über den App-Dialog statt window.confirm nach (Freigabe 17.10.2026), Text unverändert.
import React, { useEffect, useState } from 'react';
import { CheckCircle, Eye, HandArrowDown, HandArrowUp, Info, Signature, type Icon } from '@phosphor-icons/react';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { istTouch } from './assistent/Rundgangteile';
import { supabase } from '../../../lib/supabase/client';
import type { LokalerEntwurf, UnterschriftEntwurf } from '../../../lib/uebergabe/lokal';
import { entferneZeugen } from '../../../lib/uebergabe/lokal';
import type { Partei, Unterschrift } from '../../../lib/uebergabe/typen';
import {
  pruefeAbschlussBereit,
  uebernimmUnterschriftInParteien,
  gehoertZu,
  parteiLabel,
  fuegeZeugenHinzu,
  ersetzeUnterschriftBlob,
} from '../../../lib/uebergabe/unterschriften';
import UnterschriftPad from '../../../components/ui/UnterschriftPad';
import { useViewport } from '../../../components/island/hooks';
import { istKompakt } from '../../../components/island/island-size';
import ZeugenKarten from './ZeugenKarten';

interface SchrittUnterschriftenProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
}

const ROLLEN_STIL: Record<Partei['rolle'], { icon: Icon; farbe: string }> = {
  uebergeber: { icon: HandArrowUp, farbe: '#ff9500' },
  uebernehmer: { icon: HandArrowDown, farbe: '#34c759' },
  zeuge: { icon: Eye, farbe: '#8e8e93' },
};

const FELD: React.CSSProperties = {
  height: 44, padding: '0 12px', borderRadius: 13, border: 'none', outline: 'none', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--ink)', minWidth: 0, width: '100%',
};

// Anzeige-/Speicherformat für u.datum ist deutsch (TT.MM.JJJJ) — <input type="date">
// arbeitet intern aber mit JJJJ-MM-TT, deshalb hier hin- und zurückgewandelt.
const deutschZuIso = (v: string): string => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
const isoZuDeutsch = (v: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
};
const heuteDeutsch = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

async function dataUrlZuBlob(dataUrl: string): Promise<Blob> {
  const antwort = await fetch(dataUrl);
  return antwort.blob();
}

// Schlüssel einer Unterschriften-Karte: die Partei-Kennung, sonst (Altdaten ohne
// `pid`) die Rolle. Zentral hier, statt an jeder Stelle wiederholt — muss überall
// identisch berechnet werden (unterschrift_entwuerfe-Schlüssel, aktivesSignierfeld,
// neuUnterschreiben-Set).
const kartenSchluessel = (partei: Partei): string => partei.pid ?? partei.rolle;

// Effektiver Stand einer Unterschriften-Karte: der in entwurf.unterschrift_entwuerfe
// gespeicherte Wert (sobald einmal geschrieben — auch ein leerer String zählt als
// "bereits angefasst"), sonst derselbe Default wie zuvor. Als freie Funktion (nicht
// im Komponenten-Closure) nutzbar, damit sie sowohl beim Rendern als auch innerhalb
// von setEntwurf-Updatern denselben, garantiert aktuellen Entwurfsstand liest. Nimmt
// die PARTEI (nicht mehr die Rolle) entgegen — der Name-Default kommt aus derselben
// Partei im jeweils übergebenen `entwurf`, nicht aus der (ggf. veralteten) Partei aus
// dem Render-Closure.
function effektiveFelder(entwurf: LokalerEntwurf, partei: Partei): UnterschriftEntwurf {
  const schluessel = kartenSchluessel(partei);
  const gespeichert = entwurf.unterschrift_entwuerfe?.[schluessel];
  if (gespeichert) return gespeichert;
  const aktuellePartei = entwurf.protokoll.parteien.find(p => kartenSchluessel(p) === schluessel);
  return {
    name: aktuellePartei?.name ?? '',
    ort: '',
    datum: heuteDeutsch(),
  };
}

// URL im Effekt anlegen (wie FotoVorschau): mit useMemo würde sie beim erneuten Einhängen
// schon widerrufen sein und das Bild bliebe leer.
const UnterschriftVorschau: React.FC<{ blob: Blob }> = ({ blob }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return (
    <div className="flex items-center justify-center" style={{ height: 96, borderRadius: 16, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      {url && <img src={url} alt="Unterschrift" style={{ maxWidth: '100%', maxHeight: 86 }} />}
    </div>
  );
};

const SchrittUnterschriften: React.FC<SchrittUnterschriftenProps> = ({ entwurf, setEntwurf }) => {
  const { protokoll } = entwurf;
  // Bewusst KEINE eigene Breiten-Abfrage: istKompakt ist die etablierte
  // Vollbild-Entscheidung der App (components/island/island-size.ts, schmal ODER
  // kurz) und erkennt auch das Querformat (z. B. 844×390 — breit genug, aber ohne
  // Höhe für ein bequemes Unterschriftenfeld). Gleiche Umstellung wie zuvor bei
  // pages/public/SignierenSeite.tsx.
  const viewport = useViewport();
  const istKompaktModus = istKompakt(viewport.width, viewport.height);
  // Beide Sets/States sind Kartenschlüssel (pid, s. kartenSchluessel), nicht mehr
  // die Rolle — s. Kommentarblock „Task 3" oben.
  const [aktivesSignierfeld, setAktivesSignierfeld] = useState<string | null>(null);
  const [neuUnterschreiben, setNeuUnterschreiben] = useState<Set<string>>(new Set());
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const touch = istTouch();

  // Ort aus der Stadt des gewählten Objekts vorbelegen — überschreibt keine bereits
  // begonnene Handeingabe (Feld ist dann nicht mehr leer). Schreibt über setEntwurf,
  // damit die Vorbelegung selbst schon Teil des persistierten Entwurfs wird. Läuft
  // über protokoll.parteien (statt der früheren festen ALLE_ROLLEN-Liste) — damit
  // auch dann korrekt, wenn zum Zeitpunkt des Fetches bereits Zeugen hinzugefügt
  // wurden.
  useEffect(() => {
    const objektId = entwurf.protokoll.objekt_id;
    if (!objektId) return;
    let abgebrochen = false;
    (async () => {
      const { data } = await supabase.from('objekte').select('stadt').eq('id', objektId).maybeSingle();
      if (abgebrochen || !data?.stadt) return;
      setEntwurf(e => {
        const naechste = { ...e.unterschrift_entwuerfe };
        let geaendert = false;
        for (const partei of e.protokoll.parteien) {
          const schluessel = kartenSchluessel(partei);
          const aktuell = effektiveFelder(e, partei);
          if (!aktuell.ort.trim()) {
            naechste[schluessel] = { ...aktuell, ort: data.stadt };
            geaendert = true;
          }
        }
        return geaendert ? { ...e, unterschrift_entwuerfe: naechste } : e;
      });
    })();
    return () => { abgebrochen = true; };
  }, [entwurf.protokoll.objekt_id, setEntwurf]);

  const aendereFeld = (partei: Partei, feld: keyof UnterschriftEntwurf, wert: string) => {
    const schluessel = kartenSchluessel(partei);
    setEntwurf(e => ({
      ...e,
      unterschrift_entwuerfe: { ...e.unterschrift_entwuerfe, [schluessel]: { ...effektiveFelder(e, partei), [feld]: wert } },
    }));
  };

  const erneutUnterschreiben = (partei: Partei) => {
    const schluessel = kartenSchluessel(partei);
    const bestehend = protokoll.unterschriften.find(u => gehoertZu(u, partei));
    if (bestehend) {
      setEntwurf(e => ({
        ...e,
        unterschrift_entwuerfe: { ...e.unterschrift_entwuerfe, [schluessel]: { name: bestehend.name, ort: bestehend.ort, datum: bestehend.datum } },
      }));
    }
    setNeuUnterschreiben(prev => new Set(prev).add(schluessel));
  };

  const unterschriftUebernehmen = async (partei: Partei, dataUrl: string) => {
    const schluessel = kartenSchluessel(partei);
    const blob = await dataUrlZuBlob(dataUrl);
    const pngPfad = `${entwurf.id}/unterschriften/${schluessel}.png`;
    setEntwurf(e => {
      const felder = effektiveFelder(e, partei);
      const neueUnterschrift: Unterschrift = {
        rolle: partei.rolle,
        // Ohne die Kennung wäre bei zwei Zeugen nicht unterscheidbar, wer
        // unterschrieben hat — gehoertZu() bräuchte sonst wieder den mehrdeutigen
        // Rollen-Rückfall.
        pid: partei.pid,
        name: felder.name.trim(),
        ort: felder.ort.trim(),
        datum: felder.datum,
        png_path: pngPfad,
        signiert_am: new Date().toISOString(),
      };
      // MINOR 4 (Abschluss-Review): ersetzeUnterschriftBlob() räumt einen abweichenden
      // ALTEN Pfad mit ab — s. Kommentar dort (lib/uebergabe/unterschriften.ts) für den
      // Alt-Entwurf-Fall, der das auslöst.
      const bestehend = e.protokoll.unterschriften.find(u => gehoertZu(u, partei));
      const unterschriften = ersetzeUnterschriftBlob(e.unterschriften, pngPfad, blob, bestehend?.png_path);
      return {
        ...e,
        unterschriften,
        protokoll: {
          ...e.protokoll,
          // Fund W1 (Review): Der NAME wandert in die zugehörige Partei zurück — sonst
          // fehlt z. B. der beim Einzug immer leer angelegte Übergeber im PDF-Abschnitt
          // „Beteiligte", obwohl er auf der Unterschriftsseite steht (s.
          // lib/uebergabe/unterschriften.ts). Der ORT bleibt bewusst draußen
          // (Rollout-Fund 3): er ist oben aus der Stadt des Objekts vorbelegt und wäre als
          // „Anschrift" des Übergebers im Beweisdokument schlicht falsch.
          //
          // CRITICAL 1 (Abschluss-Review): uebernimmUnterschriftInParteien findet seine
          // Partei inzwischen über die Kennung (Rückfall auf die Rolle nur ohne Kennung,
          // s. lib/uebergabe/unterschriften.ts) und wäre für Zeugen damit an sich sicher.
          // Für Zeugen bleibt hier trotzdem der direkte, gezielte Schreibweg über die PID
          // stehen: `nur_unterzeichner` darf an einem Zeugen ohnehin nie entstehen
          // (istVermieterseite liefert für 'zeuge' immer false), der eigene Zweig spart
          // also nur den unnötigen Umweg — kein Sicherheitsnetz mehr wie zuvor.
          parteien:
            partei.rolle === 'zeuge'
              ? e.protokoll.parteien.map(p => (p.pid === partei.pid && !p.name.trim() ? { ...p, name: felder.name.trim() } : p))
              : uebernimmUnterschriftInParteien(e.protokoll.parteien, partei, felder, e.protokoll.art),
          unterschriften: [...e.protokoll.unterschriften.filter(u => !gehoertZu(u, partei)), neueUnterschrift],
        },
      };
    });
    setNeuUnterschreiben(prev => {
      const naechste = new Set(prev);
      naechste.delete(schluessel);
      return naechste;
    });
    setAktivesSignierfeld(null);
  };

  // Zeugen entfernen, mit Rückfrage, sobald bereits unterschrieben wurde — sonst
  // würde eine geleistete Unterschrift stillschweigend verworfen.
  const zeugenEntfernen = async (partei: Partei) => {
    const hatUnterschrift = protokoll.unterschriften.some(u => gehoertZu(u, partei));
    if (
      hatUnterschrift &&
      !(await bestaetige({
        nachricht: `${parteiLabel(partei, protokoll.parteien)} entfernen?\n\nDie bereits geleistete Unterschrift wird dabei verworfen.`,
        label: 'Entfernen',
      }))
    ) {
      return;
    }
    // IMPORTANT 3 (Abschluss-Review): kein `!` mehr — entferneZeugen() weist eine fehlende
    // `pid` selbst hart zurück (Übergeber/Übernehmer dürfen nie über eine „gefundene" erste
    // kennungslose Partei mitgelöscht werden), die Zusicherung wandert damit an den Ort, der
    // sie tatsächlich garantieren muss.
    setEntwurf(e => entferneZeugen(e, partei.pid));
  };

  const status = pruefeAbschlussBereit(protokoll);

  // Eine Karte für Übergeber/Übernehmer UND Zeugen — dieselbe Darstellung, damit es
  // im ganzen Schritt nur eine Kartenversion gibt (ZeugenKarten bekommt sie als
  // renderKarte-Prop hereingereicht, statt sie selbst nachzubauen).
  const renderKarte = (partei: Partei, fuss?: React.ReactNode): React.ReactNode => {
    const schluessel = kartenSchluessel(partei);
    const bestehend = protokoll.unterschriften.find(u => gehoertZu(u, partei));
    const zeigtSigniert = !!bestehend && !neuUnterschreiben.has(schluessel);
    const blob = bestehend ? entwurf.unterschriften[bestehend.png_path] : undefined;
    const felder = effektiveFelder(entwurf, partei);
    const nameFehlt = !felder.name.trim();
    const label = parteiLabel(partei, protokoll.parteien);
    const stil = ROLLEN_STIL[partei.rolle];
    const I = stil.icon;
    const knopfStil: React.CSSProperties = {
      height: 46, gap: 8, borderRadius: 14, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
    };

    return (
      <section key={schluessel} aria-label={label} className="flex flex-col"
        style={{ gap: 10, padding: 16, borderRadius: 24, background: 'var(--glass)',
          boxShadow: zeigtSigniert ? 'inset 0 0 0 2px var(--ok), var(--glass-sh)' : 'var(--glass-sh)' }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: stil.farbe, color: '#fff' }}>
            <I size={17} weight="fill" />
          </span>
          <span className="min-w-0 flex-1 truncate" style={{ fontSize: 15, fontWeight: 800 }}>{label}</span>
          {partei.rolle === 'zeuge' && (
            <span className="inline-flex shrink-0 items-center" style={{ height: 22, padding: '0 9px', borderRadius: 11, fontSize: 11, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>Freiwillig</span>
          )}
          {zeigtSigniert && (
            <span className="inline-flex shrink-0 items-center" style={{ height: 24, padding: '0 10px', gap: 5, borderRadius: 12, fontSize: 11.5, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <CheckCircle size={13} weight="fill" />Unterschrieben
            </span>
          )}
        </div>

        {zeigtSigniert && bestehend ? (
          <>
            {blob ? <UnterschriftVorschau blob={blob} /> : (
              <p className="m-0 text-center" style={{ padding: '16px 0', fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>Unterschrift-Datei ist auf diesem Gerät nicht (mehr) vorhanden.</p>
            )}
            <div className="flex flex-col" style={{ gap: 2, fontSize: 13, fontWeight: 700 }}>
              <span className="truncate">{bestehend.name || '—'}</span>
              <span style={{ color: 'var(--ink3)', fontWeight: 600 }}>{bestehend.ort || '—'} · {bestehend.datum}</span>
            </div>
            <button type="button" onClick={() => erneutUnterschreiben(partei)} className="inline-flex items-center justify-center"
              data-tip="Erneut unterschreiben – Unterschriftenfeld noch einmal öffnen"
              style={{ ...knopfStil, cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
              <Signature size={16} weight="bold" />Erneut unterschreiben
            </button>
          </>
        ) : (
          <>
            <div className="grid" style={{ gap: 8, gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)' }}>
              <input placeholder="Name" aria-label={`Name ${label}`} value={felder.name} onChange={e => aendereFeld(partei, 'name', e.target.value)} style={FELD} />
              <input placeholder="Ort" aria-label={`Ort ${label}`} value={felder.ort} onChange={e => aendereFeld(partei, 'ort', e.target.value)} style={FELD} />
            </div>
            <input
              type="date"
              aria-label={`Datum ${label}`}
              value={deutschZuIso(felder.datum)}
              onChange={e => aendereFeld(partei, 'datum', isoZuDeutsch(e.target.value))}
              style={FELD}
            />
            <button
              type="button"
              onClick={() => setAktivesSignierfeld(schluessel)}
              disabled={nameFehlt}
              data-tip={nameFehlt ? 'Name erforderlich, bevor unterschrieben werden kann' : 'Unterschreiben – Unterschriftenfeld öffnen'}
              className="inline-flex items-center justify-center"
              style={{ ...knopfStil, cursor: nameFehlt ? 'not-allowed' : 'pointer', opacity: nameFehlt ? 0.45 : 1,
                background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: nameFehlt ? 'none' : 'var(--acc-sh)' }}
            >
              <Signature size={16} weight="bold" />Unterschreiben
            </button>
            {nameFehlt && (
              // Neutral statt Warn-Orange: dieses Feld ist bei einem Zeugen FREIWILLIG,
              // die Farbe der echten Sperrmeldung (unten) hier zu wiederholen suggerierte
              // fälschlich eine offene Pflicht. Der Satz sagt bei Zeugen zusätzlich, dass er
              // nur bei tatsächlicher Unterschrift zählt.
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
                {partei.rolle === 'zeuge'
                  ? 'Nur nötig, wenn dieser Zeuge unterschreiben soll: bitte zuerst den Namen eintragen.'
                  : 'Bitte zuerst den Namen eintragen.'}
              </span>
            )}
          </>
        )}
        {fuss}
      </section>
    );
  };

  const vertragsparteien = protokoll.parteien.filter(p => p.rolle !== 'zeuge');
  const zeugen = protokoll.parteien.filter(p => p.rolle === 'zeuge');
  const aktivePartei = aktivesSignierfeld
    ? protokoll.parteien.find(p => kartenSchluessel(p) === aktivesSignierfeld) ?? null
    : null;

  const raster: React.CSSProperties = { display: 'grid', gap: 12, alignItems: 'start', // Höchstens zwei Spalten (je ≥ 300 px), damit ein einzelner Zeuge nicht über die ganze Breite läuft.
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, max(300px, calc((100% - 12px) / 2))), 1fr))' };

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <div style={raster}>
        {vertragsparteien.map(p => renderKarte(p))}
      </div>

      <ZeugenKarten
        zeugen={zeugen}
        alleParteien={protokoll.parteien}
        renderKarte={renderKarte}
        raster={raster}
        touch={touch}
        onHinzufuegen={() => setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, parteien: fuegeZeugenHinzu(e.protokoll.parteien) } }))}
        onEntfernen={p => { void zeugenEntfernen(p); }}
      />

      <div role="status" className="flex items-center" style={{ gap: 10, padding: '12px 14px', borderRadius: 18, background: status.bereit ? 'var(--ok-soft)' : 'var(--fill)',
        boxShadow: status.bereit ? 'none' : 'inset 0 0 0 1px var(--line)' }}>
        {status.bereit
          ? <CheckCircle size={20} weight="fill" color="var(--ok)" className="shrink-0" />
          : <Info size={20} weight="fill" color="var(--ink2)" className="shrink-0" />}
        <span style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4, color: status.bereit ? 'var(--ok)' : 'var(--ink2)' }}>
          {status.bereit ? 'Übergeber und Übernehmer haben unterschrieben — bereit zum Abschluss.' : `${status.grund} Zeugen sind freiwillig.`}
        </span>
      </div>

      {aktivePartei && (
        <UnterschriftPad
          huelle="sheet"
          istMobil={istKompaktModus}
          titel={`Unterschrift – ${parteiLabel(aktivePartei, protokoll.parteien)}`}
          onSchliessen={() => setAktivesSignierfeld(null)}
          onUebernehmen={dataUrl => { void unterschriftUebernehmen(aktivePartei, dataUrl); }}
        />
      )}
      {dialoge}
    </div>
  );
};

export default SchrittUnterschriften;
